import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import Layout from "@/components/app/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Pemasukan from "@/pages/Pemasukan";
import KategoriPage from "@/pages/KategoriPage";
import MasterPembeli from "@/pages/MasterPembeli";
import Pengeluaran from "@/pages/Pengeluaran";
import LaporanSemua from "@/pages/LaporanSemua";
import LaporanPPh05 from "@/pages/LaporanPPh05";
import LaporanPPhNormal from "@/pages/LaporanPPhNormal";
import UsersPage from "@/pages/UsersPage";
import AuditLog from "@/pages/AuditLog";
import Trash from "@/pages/Trash";
import Settings from "@/pages/Settings";
import LabaRugi from "@/pages/LabaRugi";
import Neraca from "@/pages/Neraca";
import ArusKas from "@/pages/ArusKas";
import Profile from "@/pages/Profile";
import SuperAdminUsers from "@/pages/SuperAdminUsers";
import SuperAdminAudit from "@/pages/SuperAdminAudit";
import SuperAdminSecurity from "@/pages/SuperAdminSecurity";
import SuperAdminEmail from "@/pages/SuperAdminEmail";
import SuperAdminLangganan from "@/pages/SuperAdminLangganan";
import Langganan from "@/pages/Langganan";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import SuperAdminBusinessTypes from "@/pages/SuperAdminBusinessTypes";
import Produksi from "@/pages/Produksi";
import KayuMasuk from "@/pages/KayuMasuk";
import BatchBakar from "@/pages/BatchBakar";
import PembelianArang from "@/pages/PembelianArang";
import ProduksiBriket from "@/pages/ProduksiBriket";
import BahanBriket from "@/pages/BahanBriket";
import TransferStok from "@/pages/TransferStok";
import SuperAdminPabrik from "@/pages/SuperAdminPabrik";
import Dividen from "@/pages/Dividen";
import HutangPiutang from "@/pages/HutangPiutang";
import PiutangReminder from "@/pages/PiutangReminder";
import PemasukanPribadi from "@/pages/PemasukanPribadi";
import PengeluaranPribadi from "@/pages/PengeluaranPribadi";
import PajakPribadi from "@/pages/PajakPribadi";
import HartaPage from "@/pages/HartaPage";
import AsistenSptCoretax from "@/pages/AsistenSptCoretax";
import PanduanPajak from "@/pages/PanduanPajak";
import UtangPage from "@/pages/UtangPage";
import TanggunganPage from "@/pages/TanggunganPage";
import PengaturanPajakPribadi from "@/pages/PengaturanPajakPribadi";
import ImporMutasi from "@/pages/ImporMutasi";
import AkunPage from "@/pages/AkunPage";
import MutasiAkun from "@/pages/MutasiAkun";
import TransferPage from "@/pages/TransferPage";
import KreditPajakPage from "@/pages/KreditPajakPage";
import DashboardPribadi from "@/pages/DashboardPribadi";
import WorkspaceOnly from "@/components/app/WorkspaceOnly";
import SuperAdminOnly from "@/components/app/SuperAdminOnly";
import EnterToSave from "@/components/app/EnterToSave";
import RiwayatBayarPajak from "@/pages/RiwayatBayarPajak";
import BackupPage from "@/pages/BackupPage";
import { firstAllowedPath } from "@/lib/menus";

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <Navigate to="/superadmin/users" replace/>;
  return <Navigate to={firstAllowedPath(user)} replace/>;
}

function DashboardSwitch() {
  const { user } = useAuth();
  return user?.workspace_type === "pribadi" ? <DashboardPribadi/> : <Dashboard/>;
}

function PemasukanSwitch() {
  const { user } = useAuth();
  return user?.workspace_type === "pribadi" ? <PemasukanPribadi/> : <Pemasukan/>;
}

function PengeluaranSwitch() {
  const { user } = useAuth();
  return user?.workspace_type === "pribadi" ? <PengeluaranPribadi/> : <Pengeluaran/>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors/>
          <EnterToSave/>
          <Routes>
            <Route path="/login" element={<Login/>}/>
            <Route path="/forgot-password" element={<ForgotPassword/>}/>
            <Route path="/register" element={<Register/>}/>
            <Route path="/verify-email" element={<VerifyEmail/>}/>
            <Route path="/reset-password" element={<ResetPassword/>}/>
            <Route element={<ProtectedRoute><Layout/></ProtectedRoute>}>
              <Route path="/" element={<HomeRedirect/>}/>
              <Route path="/dashboard" element={<WorkspaceOnly menu="dashboard"><DashboardSwitch/></WorkspaceOnly>}/>
              <Route path="/produksi" element={<WorkspaceOnly menu="produksi"><Produksi/></WorkspaceOnly>}/>
              <Route path="/produksi/kayu" element={<WorkspaceOnly menu="produksi_kayu"><KayuMasuk/></WorkspaceOnly>}/>
              <Route path="/produksi/bakar" element={<WorkspaceOnly menu="produksi_bakar"><BatchBakar/></WorkspaceOnly>}/>
              <Route path="/produksi/pembelian" element={<WorkspaceOnly menu="produksi_pembelian"><PembelianArang/></WorkspaceOnly>}/>
              <Route path="/produksi/briket" element={<WorkspaceOnly menu="produksi_briket"><ProduksiBriket/></WorkspaceOnly>}/>
              <Route path="/produksi/bahan" element={<WorkspaceOnly menu="produksi_bahan"><BahanBriket/></WorkspaceOnly>}/>
              <Route path="/produksi/transfer" element={<WorkspaceOnly menu="produksi_transfer"><TransferStok/></WorkspaceOnly>}/>
              <Route path="/produksi/kategori" element={<WorkspaceOnly menu="kategori_produk"><KategoriPage tipe="produk" title="Input Kategori Produk" subtitle="Produk milik pabrik aktif. Stok & HPP dihitung terpisah per produk." testidPrefix="kat-produk"/></WorkspaceOnly>}/>
              <Route path="/pajak/riwayat-bayar" element={<WorkspaceOnly menu="riwayat_bayar"><RiwayatBayarPajak/></WorkspaceOnly>}/>
              <Route path="/piutang" element={<WorkspaceOnly menu="piutang"><PiutangReminder/></WorkspaceOnly>}/>
              <Route path="/pemasukan" element={<WorkspaceOnly menu="pemasukan"><PemasukanSwitch/></WorkspaceOnly>}/>
              <Route path="/pemasukan/kategori" element={<WorkspaceOnly menu="kategori_pemasukan"><KategoriPage tipe="pemasukan" title="Input Kategori Pemasukan" subtitle="Kelola kategori untuk transaksi pemasukan." testidPrefix="kat-in"/></WorkspaceOnly>}/>
              <Route path="/pemasukan/pembeli" element={<WorkspaceOnly menu="pembeli"><MasterPembeli/></WorkspaceOnly>}/>
              <Route path="/pengeluaran" element={<WorkspaceOnly menu="pengeluaran"><PengeluaranSwitch/></WorkspaceOnly>}/>
              <Route path="/pengeluaran/kategori" element={<WorkspaceOnly menu="kategori_pengeluaran"><KategoriPage tipe="pengeluaran" title="Input Kategori Pengeluaran" subtitle="Kelola kategori untuk transaksi pengeluaran." testidPrefix="kat-out"/></WorkspaceOnly>}/>
              <Route path="/laporan/semua" element={<WorkspaceOnly menu="laporan_semua"><LaporanSemua/></WorkspaceOnly>}/>
              <Route path="/laporan/pph-05" element={<WorkspaceOnly menu="pph_05"><LaporanPPh05/></WorkspaceOnly>}/>
              <Route path="/laporan/pph-normal" element={<WorkspaceOnly menu="pph_normal"><LaporanPPhNormal/></WorkspaceOnly>}/>
              <Route path="/laporan/laba-rugi" element={<WorkspaceOnly menu="laba_rugi"><LabaRugi/></WorkspaceOnly>}/>
              <Route path="/laporan/neraca" element={<WorkspaceOnly menu="neraca"><Neraca/></WorkspaceOnly>}/>
              <Route path="/laporan/arus-kas" element={<WorkspaceOnly menu="arus_kas"><ArusKas/></WorkspaceOnly>}/>
              <Route path="/laporan/dividen" element={<WorkspaceOnly menu="dividen"><Dividen/></WorkspaceOnly>}/>
              <Route path="/hutang-piutang" element={<WorkspaceOnly menu="hutang_piutang"><HutangPiutang/></WorkspaceOnly>}/>
              <Route path="/hutang-piutang/kategori" element={<WorkspaceOnly menu="hutang_piutang"><KategoriPage tipe="hutang_piutang" title="Input Kategori Utang & Piutang" subtitle="Kelola kategori untuk transaksi utang & piutang." testidPrefix="kat-hp"/></WorkspaceOnly>}/>
              <Route path="/pribadi/pajak" element={<WorkspaceOnly menu="pph_op"><PajakPribadi/></WorkspaceOnly>}/>
              <Route path="/pribadi/harta" element={<WorkspaceOnly menu="harta"><HartaPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/spt-coretax" element={<WorkspaceOnly menu={["spt_coretax", "pph_op"]}><AsistenSptCoretax/></WorkspaceOnly>}/>
              <Route path="/pribadi/panduan-pajak" element={<WorkspaceOnly menu={["spt_coretax", "pph_op"]}><PanduanPajak/></WorkspaceOnly>}/>
              <Route path="/pribadi/utang" element={<WorkspaceOnly menu="utang"><UtangPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/tanggungan" element={<WorkspaceOnly menu="tanggungan"><TanggunganPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/pengaturan-pajak" element={<WorkspaceOnly menu="pajak_pengaturan"><PengaturanPajakPribadi/></WorkspaceOnly>}/>
              <Route path="/pribadi/akun" element={<WorkspaceOnly menu="akun"><AkunPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/akun/:id/mutasi" element={<WorkspaceOnly menu="akun"><MutasiAkun/></WorkspaceOnly>}/>
              <Route path="/kas-bank/akun" element={<WorkspaceOnly menu="akun"><AkunPage/></WorkspaceOnly>}/>
              <Route path="/kas-bank/akun/:id/mutasi" element={<WorkspaceOnly menu="akun"><MutasiAkun/></WorkspaceOnly>}/>
              <Route path="/kas-bank/transfer" element={<WorkspaceOnly menu="transfer"><TransferPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/transfer" element={<WorkspaceOnly menu="transfer"><TransferPage/></WorkspaceOnly>}/>
              <Route path="/pribadi/impor-mutasi" element={<WorkspaceOnly menu="impor_mutasi"><ImporMutasi/></WorkspaceOnly>}/>
              <Route path="/pribadi/kredit-pajak" element={<WorkspaceOnly menu="kredit_pajak"><KreditPajakPage/></WorkspaceOnly>}/>
              <Route path="/pengaturan" element={<WorkspaceOnly menu="pengaturan"><Settings/></WorkspaceOnly>}/>
              <Route path="/pengaturan/users" element={<WorkspaceOnly><UsersPage/></WorkspaceOnly>}/>
              <Route path="/pengaturan/audit" element={<WorkspaceOnly><AuditLog/></WorkspaceOnly>}/>
              <Route path="/pengaturan/backup" element={<WorkspaceOnly><BackupPage/></WorkspaceOnly>}/>
              <Route path="/pengaturan/sampah" element={<WorkspaceOnly><Trash/></WorkspaceOnly>}/>
              <Route path="/pengaturan/profil" element={<Profile/>}/>
              <Route path="/superadmin/users" element={<SuperAdminOnly><SuperAdminUsers/></SuperAdminOnly>}/>
              <Route path="/superadmin/audit" element={<SuperAdminOnly><SuperAdminAudit/></SuperAdminOnly>}/>
              <Route path="/superadmin/security" element={<SuperAdminOnly><SuperAdminSecurity/></SuperAdminOnly>}/>
              <Route path="/superadmin/email" element={<SuperAdminOnly><SuperAdminEmail/></SuperAdminOnly>}/>
              <Route path="/superadmin/langganan" element={<SuperAdminOnly><SuperAdminLangganan/></SuperAdminOnly>}/>
              <Route path="/langganan" element={<WorkspaceOnly><Langganan/></WorkspaceOnly>}/>
              <Route path="/superadmin/pabrik" element={<SuperAdminOnly><SuperAdminPabrik/></SuperAdminOnly>}/>
              <Route path="/superadmin/business-types" element={<SuperAdminOnly><SuperAdminBusinessTypes/></SuperAdminOnly>}/>
            </Route>
            <Route path="*" element={<Navigate to="/" replace/>}/>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
